import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { type IConversation, ReadStatus } from 'src/api/landlord-messages-api';
import {
    type ISnippet,
    EMessageChannel,
    isMessageChannel,
    useCreateActivity,
    useLead,
    useLoggedInUser,
    useLeadMessages,
    useSendMessage,
    useShowings,
    useUpdateConversationsReadStatus,
    useFacebookIntegration,
    useProperty,
    useTeamSettings,
    useSnippets,
    useLeadStageHistory,
} from 'src/services/api';
import { LeadMessagesPageContext } from 'src/pages/landlord/leads/messages/context/context';
import Alert from '@mui/material/Alert';
import LinearProgress from '@mui/material/LinearProgress';
import { useUserSettings } from 'src/services/api/users';
import { LeadUtils } from 'src/utils/LeadUtils';
import { searchSnippets } from 'src/pages/landlord/leads/utils/search-snippets';
import { usePersistentState } from 'src/services/storage/use-persistent-state';
import { detectLeadLanguage, OUTBOUND_TRANSLATABLE_LANGS } from 'src/pages/landlord/leads/messages/utils/detect-lead-language';
import dayjs from 'dayjs';

const basePath = '/landlord/inner/messages/from-lead';

const getConversationPath = (channel?: EMessageChannel, leadUuid?: string, backPath?: string) => {
    if (!leadUuid || !channel) {
        return;
    }

    let path = `${basePath}?lead_uuid=${leadUuid}&channel=${channel}&show_navigation=true`;
    if (backPath) {
        path += `&back_path=${encodeURIComponent(backPath)}`;
    }
    return path;
};

const REFETCH_INTERVAL = 3000;

type Props = {
    children: React.ReactNode;
};

export default function LeadMessagesPageProvider({ children }: Props) {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const leadUuid = searchParams.get('lead_uuid') as string;
    const channel = searchParams.get('channel') as EMessageChannel;
    const showNavigation = Boolean(searchParams.get('show_navigation'));

    const { data: facebookIntegration } = useFacebookIntegration();

    const setChannel = (newChannel: EMessageChannel, replace = false) => {
        setSearchParams(prev => {
            const newSearchParams = new URLSearchParams(prev);
            newSearchParams.set('channel', newChannel);
            return newSearchParams.toString();
        }, { replace });
    };

    if (!leadUuid) {
        console.error('Lead UUID must be set in URL params');

        return (
            <Alert severity="warning">
                Lead UUID must be set
            </Alert>
        );
    }

    if (channel && !isMessageChannel(channel)) {
        console.error(`Invalid send channel: ${channel}`);

        return (
            <Alert severity="warning">
                Invalid message channel: {channel || 'Channel not set'}.
                It must be either {EMessageChannel.SMS}, {EMessageChannel.EMAIL} or {EMessageChannel.FACEBOOK_MESSENGER}.
            </Alert>
        );
    }

    const { data: loggedInUser } = useLoggedInUser();
    const { data: userSettings } = useUserSettings();
    const { data: teamSettings } = useTeamSettings();
    const { data: snippets } = useSnippets();
    const { data: showings } = useShowings({ whole_team: true });
    const { data: lead } = useLead(leadUuid);
    const { data: property } = useProperty(lead?.property_id);
    const { data: messagesResponse } = useLeadMessages(
        leadUuid,
        { refetchInterval: REFETCH_INTERVAL, include_attachments: true }
    );
    const { data: stageHistory } = useLeadStageHistory(leadUuid);
    const stages = stageHistory?.stages ?? [];
    const interests = stageHistory?.interests ?? [];
    const messages = messagesResponse?.messages;
    const attachments = messagesResponse?.attachments;

    const { mutateAsync: updateReadStatus } = useUpdateConversationsReadStatus();

    const { mutateAsync: sendMessage, isLoading: isSendingMessage } = useSendMessage();
    const { mutateAsync: createActivity } = useCreateActivity();

    const [replyInput, setReplyInputState] = useState('');
    const [appliedSnippet, setAppliedSnippet] = useState<{ snippet: ISnippet, seededMessage: string } | undefined>(undefined);
    const [isSnippetsModalOpen, setIsSnippetsModalOpen] = useState(false);

    // Search state for snippets
    const [search, setSearch] = useState('');

    const [conversations, saveConversationsToLocalStorage] = usePersistentState(
        'conversations',
        [] as IConversation[],
        (value): value is IConversation[] => Array.isArray(value)
    );

    // Reset the conversations in local storage if the `show_navigation` param is not present
    useEffect(() => {
        if (!showNavigation) {
            saveConversationsToLocalStorage([]);
        }
    }, [showNavigation]);

    const [isTranslateOutboundEnabled, setIsTranslateOutboundEnabled, , isTranslatePrefReady] =
        usePersistentState<boolean | null>(
            `translate-outbound-${leadUuid}`,
            null,
            (v): v is boolean | null => v === null || typeof v === 'boolean'
        );

    const detectedLeadLang = useMemo(
        () => (messages ? detectLeadLanguage(messages) : null),
        [messages]
    );

    // Auto-enable translation once prefs load, but only if the user has never explicitly set a preference
    useEffect(() => {
        if (!isTranslatePrefReady) {return;}
        if (isTranslateOutboundEnabled === null && detectedLeadLang !== null && OUTBOUND_TRANSLATABLE_LANGS.has(detectedLeadLang)) {
            setIsTranslateOutboundEnabled(true);
        }
    }, [isTranslatePrefReady, detectedLeadLang]);

    const filteredSnippets = useMemo(() => {
        const _filteredSnippets = snippets || [];
        if (!search) {
            return _filteredSnippets;
        }

        return searchSnippets(_filteredSnippets, search);
    }, [snippets, search]);

    const currentConversationIndex = conversations.findIndex((o) => o.lead_uuid === leadUuid);
    const previousConversation = conversations[currentConversationIndex - 1];
    const nextConversation = conversations[currentConversationIndex + 1];
    const conversationAfterNextConversation = conversations[currentConversationIndex + 2];

    // Prefetch the previous and next lead's messages
    useLead(previousConversation?.lead_uuid);
    useLeadMessages(previousConversation?.lead_uuid, { include_attachments: true });
    useProperty(previousConversation?.property_id);
    useLead(nextConversation?.lead_uuid);
    useLeadMessages(nextConversation?.lead_uuid, { include_attachments: true });
    useProperty(nextConversation?.property_id);
    useLead(conversationAfterNextConversation?.lead_uuid);
    useLeadMessages(conversationAfterNextConversation?.lead_uuid, { include_attachments: true });
    useProperty(conversationAfterNextConversation?.property_id);

    const previousLead = useLead(previousConversation?.lead_uuid)?.data;
    const nextLead = useLead(nextConversation?.lead_uuid)?.data;

    const backPath = searchParams.get('back_path') ?? undefined;
    const previousConversationPath = getConversationPath(previousConversation?.latest_message_channel, previousLead?.uuid, backPath);
    const nextConversationPath = getConversationPath(nextConversation?.latest_message_channel, nextLead?.uuid, backPath);

    const navigateToPreviousConversation = () => {
        if (previousConversationPath) {
            navigate(previousConversationPath);
        }
    };

    const navigateToNextConversation = () => {
        if (nextConversationPath) {
            navigate(nextConversationPath);
        }
    };

    useEffect(() => {
        updateReadStatus({
            leadUuids: [leadUuid],
            status: ReadStatus.READ,
        });
    }, [leadUuid, messages]);

    // Default the channel when none is in the URL (e.g. links from the tours
    // calendar or analytics that only pass lead_uuid). Use replace so this
    // auto-added param does not create an extra history entry that traps the
    // browser back button on the messages page.
    useEffect(() => {
        if (!channel && lead && messages) {
            setChannel(LeadUtils.getDefaultMessageChannel(lead, messages), true);
        }
    }, [channel, lead, messages]);

    const isLoading = !loggedInUser || !userSettings || !teamSettings || !snippets || (lead?.property_id && !property) || !showings || !lead || !messages || !stageHistory;
    if (isLoading) {
        return (
            <LinearProgress />
        );
    }

    const setReplyInput = (nextReplyInput: string) => {
        setReplyInputState(nextReplyInput);
        if (appliedSnippet && nextReplyInput !== appliedSnippet.seededMessage) {
            setAppliedSnippet(undefined);
        }
    };

    /**
     * @param seededMessage The exact message that was computed from the snippet for this lead/landlord/property.
     * It will have all the variables filled in and include the signature.
     */
    const setReplyInputFromSnippet = (snippet: ISnippet, seededMessage: string) => {
        setAppliedSnippet({ snippet, seededMessage });
        setReplyInputState(seededMessage);
    };

    const snippetId = appliedSnippet && replyInput === appliedSnippet.seededMessage ? appliedSnippet.snippet.id : undefined;

    const handleSendMessage = async (message: string, files?: File[]) => {
        const shouldTranslate = isTranslateOutboundEnabled === true && !!detectedLeadLang && OUTBOUND_TRANSLATABLE_LANGS.has(detectedLeadLang);

        // The signature is excluded from translation by the backend, which
        // derives it from the user's settings — nothing extra to send here.
        await sendMessage({
            leadUuid,
            channel,
            message,
            attachments: files,
            snippet_id: snippetId,
            translate_to_lang: shouldTranslate ? detectedLeadLang : undefined,
        });
        setAppliedSnippet(undefined);
        createActivity({ time: dayjs().format(), type: 'manual_message' }).catch(() => {
            console.error('Failed to create activity');
        });
    };

    return (
        <LeadMessagesPageContext.Provider value={{
            loggedInUser,
            userSettings,
            teamSettings,
            showings,
            snippets: filteredSnippets,
            stages,
            interests,
            teamMembers: loggedInUser.leasing_team_members || [],
            isFacebookConnected: !!facebookIntegration?.user_integration,

            lead,
            property,
            messages,
            attachments,

            channel,
            setChannel,
            showNavigation,

            conversations,
            previousLead,
            navigateToPreviousConversation,
            nextLead,
            navigateToNextConversation,

            replyInput,
            setReplyInput,
            setReplyInputFromSnippet,

            sendMessage: handleSendMessage,
            isSendingMessage,

            detectedLeadLang,
            isTranslateOutboundEnabled,
            setIsTranslateOutboundEnabled,

            isSnippetsModalOpen,
            setIsSnippetsModalOpen,

            search,
            setSearch,
        }}
        >
            {children}
        </LeadMessagesPageContext.Provider>
    );
}
